import { useState, useEffect } from "react";
import { doc, getDoc, updateDoc, collection, query, where, getDocs, arrayUnion } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";

export default function Profile() {
  const { currentUser } = useAuth();
  const [userData, setUserData] = useState(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [emailNotifications, setEmailNotifications] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [promoCode, setPromoCode] = useState("");
  const [redeemingCode, setRedeemingCode] = useState(false);
  const [codeMessage, setCodeMessage] = useState("");
  const [codeError, setCodeError] = useState("");


  useEffect(() => {
    async function fetchUser() {
      const snap = await getDoc(doc(db, "users", currentUser.uid));
      if (snap.exists()) {
        const data = snap.data();
        setUserData(data);
        setFirstName(data.firstName || "");
        setLastName(data.lastName || "");
        setEmailNotifications(data.emailNotifications ?? true);
        setMarketing(data.marketing ?? false);
      }
      setLoading(false);
    }
    fetchUser();
  }, [currentUser]);

  async function handleSave(e) {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) return;
    setSaving(true);
    await updateDoc(doc(db, "users", currentUser.uid), {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      emailNotifications,
      marketing,
    });
    setSuccess(true);
    setSaving(false);
    setTimeout(() => setSuccess(false), 3000);
  }

  const Checkbox = ({ checked, onChange, label, sublabel }) => (
    <label className="flex items-start gap-3 cursor-pointer">
      <div
        onClick={() => onChange(!checked)}
        className="mt-0.5 w-5 h-5 rounded flex-shrink-0 flex items-center justify-center transition-all"
        style={{
          backgroundColor: checked ? "#C8FF00" : "#F5F6F2",
          border: checked ? "2px solid #C8FF00" : "2px solid #E0E2DB",
        }}
      >
        {checked && <span style={{ color: "#0A0B0F", fontSize: "12px", fontWeight: "bold" }}>✓</span>}
      </div>
      <div>
        <span className="font-condensed text-base tracking-wide" style={{ color: "#0A0B0F" }}>{label}</span>
        {sublabel && <p className="font-condensed text-gray-400 text-sm tracking-wide mt-0.5">{sublabel}</p>}
      </div>
    </label>
  );

async function handleRedeemCode(e) {
  e.preventDefault();
  setCodeMessage("");
  setCodeError("");
  setRedeemingCode(true);

  const inputCode = promoCode.trim().toUpperCase();
  if (!inputCode) {
    setCodeError("Please enter a code.");
    setRedeemingCode(false);
    return;
  }

  try {
    // Find the code
    const q = query(
      collection(db, "premiumCodes"),
      where("code", "==", inputCode)
    );
    const snap = await getDocs(q);

    if (snap.empty) {
      setCodeError("Invalid code. Please check and try again.");
      setRedeemingCode(false);
      return;
    }

    const codeDoc = snap.docs[0];
    const codeData = codeDoc.data();

    // Already used by this user
    if (codeData.usedBy?.includes(currentUser.uid)) {
      setCodeError("You've already used this code.");
      setRedeemingCode(false);
      return;
    }

    // Already premium
    if (userData?.isPremium) {
      setCodeError("You already have premium access.");
      setRedeemingCode(false);
      return;
    }

    // Check active
    if (!codeData.isActive) {
      setCodeError("This code has been expired.");
      setRedeemingCode(false);
      return;
    }

    // Check expiry date
    if (codeData.expiresAt && codeData.expiresAt.toDate() < new Date()) {
      setCodeError("This code has expired.");
      setRedeemingCode(false);
      return;
    }

    // Check max uses
    if (codeData.maxUses && codeData.usedBy.length >= codeData.maxUses) {
      setCodeError("This code has reached its maximum number of uses.");
      setRedeemingCode(false);
      return;
    }

    // All checks passed — grant premium
    await updateDoc(doc(db, "users", currentUser.uid), {
      isPremium: true,
    });

    // Record usage on the code
    await updateDoc(doc(db, "premiumCodes", codeDoc.id), {
      usedBy: arrayUnion(currentUser.uid),
    });

    setUserData((prev) => ({ ...prev, isPremium: true }));
    setPromoCode("");
    setCodeMessage("🎉 Premium unlocked! You now have full access.");
  } catch (err) {
    setCodeError("Something went wrong. Please try again.");
  }
  setRedeemingCode(false);
}

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F0F1EC" }}>
      <Navbar />
      <div className="max-w-2xl mx-auto px-6 py-10">

        <div className="mb-10 fade-up">
          <p className="font-condensed text-gray-500 tracking-widest uppercase text-sm mb-2">Account</p>
          <h2 className="font-display text-7xl tracking-wider" style={{ color: "#0A0B0F" }}>PROFILE</h2>
        </div>

        {loading ? (
          <p className="font-condensed text-gray-400 text-xl">Loading...</p>
        ) : (
          <form onSubmit={handleSave} className="space-y-6 fade-up fade-up-delay-1">

            {success && (
              <div className="px-4 py-3 rounded-xl font-condensed tracking-wide"
                style={{ backgroundColor: "#EDFFD0", border: "1px solid #C8FF00", color: "#3A6600" }}>
                ✓ Profile updated!
              </div>
            )}

            {/* Read-only info */}
            <div className="card p-6">
              <h3 className="font-condensed text-gray-500 tracking-widest uppercase text-sm mb-6">Account Info</h3>
              <div className="space-y-5">

                <div>
                  <label className="font-condensed text-gray-400 tracking-widest text-xs uppercase block mb-1">
                    Email Address
                  </label>
                  <p className="font-condensed text-xl tracking-wide" style={{ color: "#0A0B0F" }}>
                    {currentUser.email}
                  </p>
                  <p className="font-condensed text-gray-400 text-sm mt-1">Email cannot be changed</p>
                </div>

                <div style={{ borderTop: "1px solid #E0E2DB", paddingTop: "1.25rem" }}>
                  <label className="font-condensed text-gray-400 tracking-widest text-xs uppercase block mb-1">
                    Display Name
                  </label>
                  <p className="font-condensed text-xl tracking-wide" style={{ color: "#0A0B0F" }}>
                    {userData?.displayName}
                  </p>
                  <p className="font-condensed text-sm mt-1" style={{ color: "#FF5C00" }}>
                    ⚠ Display name is permanent and cannot be changed
                  </p>
                </div>

                <div style={{ borderTop: "1px solid #E0E2DB", paddingTop: "1.25rem" }}>
                  <label className="font-condensed text-gray-400 tracking-widest text-xs uppercase block mb-2">
                    Account Type
                  </label>
                  <span className="font-condensed tracking-wide px-3 py-1 rounded-full text-sm"
                    style={userData?.isPremium
                      ? { backgroundColor: "#EDFFD0", color: "#3A6600" }
                      : { backgroundColor: "#F0F0F0", color: "#888" }}>
                    {userData?.isPremium ? "⚡ PREMIUM" : "FREE"}
                  </span>
                </div>
              </div>
            </div>

            {/* Editable name */}
            <div className="card p-6">
              <h3 className="font-condensed text-gray-500 tracking-widest uppercase text-sm mb-6">Personal Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="font-condensed text-gray-400 tracking-widest text-xs uppercase block mb-2">
                    First Name
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full rounded-xl px-4 py-3 text-lg"
                  />
                </div>
                <div>
                  <label className="font-condensed text-gray-400 tracking-widest text-xs uppercase block mb-2">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full rounded-xl px-4 py-3 text-lg"
                  />
                </div>
              </div>
            </div>

            {/* Notification preferences */}
            <div className="card p-6">
              <h3 className="font-condensed text-gray-500 tracking-widest uppercase text-sm mb-6">
                Notification Preferences
              </h3>
              <div className="space-y-5">
                <Checkbox
                  checked={emailNotifications}
                  onChange={setEmailNotifications}
                  label="Notify me about upcoming predictions"
                  sublabel="We'll email you before prediction deadlines close"
                />
                <Checkbox
                  checked={marketing}
                  onChange={setMarketing}
                  label="Receive marketing emails"
                  sublabel="News, features, and offers from Predictorz"
                />
              </div>
            </div>

        
            {/* Premium Code Redemption */}
            {!userData?.isPremium && (
              <div className="card p-6">
                <h3 className="font-condensed text-gray-500 tracking-widest uppercase text-sm mb-1">
                  Premium Code
                </h3>
                <p className="font-condensed text-gray-400 tracking-wide text-sm mb-6">
                  Have a code? Enter it here to unlock premium for free
                </p>

                {codeMessage && (
                  <div className="mb-4 px-4 py-3 rounded-xl font-condensed tracking-wide"
                    style={{ backgroundColor: "#EDFFD0", border: "1px solid #C8FF00", color: "#3A6600" }}>
                    {codeMessage}
                  </div>
                )}
                {codeError && (
                  <div className="mb-4 px-4 py-3 rounded-xl font-condensed tracking-wide text-red-600"
                    style={{ backgroundColor: "#FFF0F0", border: "1px solid #FFCCCC" }}>
                    {codeError}
                  </div>
                )}

                <div className="flex gap-3">
                  <input
                    type="text"
                    placeholder="Enter code (e.g. EARLYBIRD)"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    className="flex-1 rounded-xl px-4 py-3 text-lg"
                    style={{ fontFamily: "monospace", letterSpacing: "0.1em" }}
                  />
                  <button
                    type="button"
                    onClick={handleRedeemCode}
                    disabled={redeemingCode}
                    className="btn-lime px-6 py-3 rounded-xl"
                  >
                    {redeemingCode ? "CHECKING..." : "REDEEM"}
                  </button>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={saving}
              className="btn-lime px-8 py-4 rounded-xl text-xl w-full"
            >
              {saving ? "SAVING..." : "SAVE CHANGES"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}